import type { RouteLocationNormalized, Router } from "vue-router"

// RD-120: GA4, поток Main Front. Включение и Measurement ID задаются сборкой окружения.
const MEASUREMENT_ID_PATTERN = /^G-[A-Z0-9]+$/

// Статический перечень маршрутов: в GA уходит только нормализованный путь и нейтральное название.
const SAFE_PAGES: Record<string, { path: string; title: string }> = {
  Home: { path: "/", title: "Schedule" },
  Login: { path: "/login", title: "Login" },
  Recovery: { path: "/recovery", title: "Recovery" },
  Employees: { path: "/employees", title: "Employees" },
  Exchange: { path: "/exchange", title: "Exchange" },
  Analytics: { path: "/analytics", title: "Analytics" },
  Settings: { path: "/settings", title: "Settings" },
  NotFound: { path: "/404", title: "Not found" },
}
const UNKNOWN_PAGE = { path: "/unknown", title: "Unknown" }

type Gtag = (...args: unknown[]) => void

declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: Gtag
  }
  interface Navigator {
    standalone?: boolean
  }
}

const getMeasurementId = (): string | null => {
  const enabled = import.meta.env.VITE_GA4_ENABLED === "true"
  const measurementId = import.meta.env.VITE_GA4_MEASUREMENT_ID

  const isAllowed = enabled
    && typeof measurementId === "string"
    && MEASUREMENT_ID_PATTERN.test(measurementId)

  return isAllowed ? measurementId : null
}

const getViewportWidthRange = (width: number): string => {
  if (width < 360) return "<360"
  if (width <= 389) return "360–389"
  if (width <= 413) return "390–413"
  if (width <= 767) return "414–767"
  if (width <= 1023) return "768–1023"
  if (width <= 1279) return "1024–1279"
  if (width <= 1439) return "1280–1439"
  if (width <= 1919) return "1440–1919"
  return "≥1920"
}

const getDisplayMode = (): "standalone" | "browser" => {
  const isStandalone = window.matchMedia?.("(display-mode: standalone)").matches
    || window.navigator.standalone === true

  return isStandalone ? "standalone" : "browser"
}

const getSafePage = (route: RouteLocationNormalized) => {
  const name = route.name?.toString() || ""
  return SAFE_PAGES[name] || UNKNOWN_PAGE
}

const toSafeLocation = (base: string, path: string): string => {
  const normalizedBase = base.replace(/\/+$/, "")
  return `${window.location.origin}${normalizedBase}${path}`
}

const loadGtagScript = (measurementId: string) => {
  const script = document.createElement("script")
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`
  // Блокировщик/офлайн: просто не грузим, приложение работает дальше.
  script.onerror = () => {
    console.warn("[ga4] Google tag не загрузился")
  }
  document.head.appendChild(script)
}

export const initGa4 = (router: Router) => {
  try {
    const measurementId = getMeasurementId()
    if (!measurementId) return

    const dataLayer = (window.dataLayer = window.dataLayer || [])
    // gtag.js ожидает в dataLayer именно объект arguments.
    window.gtag = function gtag() {
      dataLayer.push(arguments)
    }
    const gtag = window.gtag

    const base = router.options.history.base || ""
    const initialPage = SAFE_PAGES.Home

    // Безопасные значения задаются до config, чтобы исходный URL/title/referrer не ушли ни в одно событие.
    gtag("set", {
      page_location: toSafeLocation(base, initialPage.path),
      page_title: initialPage.title,
      page_referrer: "",
    })
    gtag("js", new Date())
    gtag("config", measurementId, {
      send_page_view: false,
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
    })
    loadGtagScript(measurementId)

    let lastPagePath: string | null = null
    let lastPageLocation = ""

    // afterEach срабатывает и на первую навигацию, поэтому отдельного стартового page_view нет.
    router.afterEach((to, _from, failure) => {
      try {
        if (failure) return

        const page = getSafePage(to)
        // Тот же маршрут, изменение только query/hash — не новый просмотр.
        if (page.path === lastPagePath) return

        const pageLocation = toSafeLocation(base, page.path)
        const pageReferrer = lastPageLocation
        lastPagePath = page.path
        lastPageLocation = pageLocation

        const width = window.innerWidth
        const pageParams = {
          page_location: pageLocation,
          page_title: page.title,
          page_referrer: pageReferrer,
        }

        gtag("set", pageParams)
        gtag("event", "page_view", {
          ...pageParams,
          viewport_width: width,
          viewport_height: window.innerHeight,
          device_pixel_ratio: window.devicePixelRatio,
          display_mode: getDisplayMode(),
          viewport_width_range: getViewportWidthRange(width),
        })
      } catch (error) {
        console.warn("[ga4] page_view не отправлен", error)
      }
    })
  } catch (error) {
    console.warn("[ga4] инициализация не выполнена", error)
  }
}
